import { useState } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
	DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDistanceToNow } from 'date-fns'
import {
	MoreVertical,
	Camera,
	AlertTriangle,
	Maximize2,
	Lock,
	Bug,
	ChevronUp,
	ChevronDown,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { useProgramContext } from '@/context/ProgramContext'
import { canDeleteFeedback } from '@/lib/programPermissions'
import { Card, CardContent } from '@/components/ui/card'
import { useTranslation } from '@/hooks/useTranslation'

interface BugReport {
	id: string
	description: string
	status: string
	severity: string
	created_at: string
	updated_at: string
	screenshot_url: string | null
	user_metadata: {
		browser?: string
		os?: string
		device?: string
		url?: string
		screenSize?: string
	} | null
	reporter_email: string | null
	reporter_name: string | null
	assignee_user_id: string | null
	widget_id: string
	program_id: string
	widget?: {
		name: string
	}
	program?: {
		name: string
	}
}

interface BugReportTableProps {
	bugs: BugReport[]
	isLoading: boolean
	className?: string
	onBugSelect?: (bugId: string | null) => void
	isPro?: boolean
	hiddenCount?: number
	itemsPerPage?: number
	sort?: string
	onSortChange?: (sort: string) => void
}

type BugStatus = 'new' | 'in_progress' | 'resolved' | 'closed' | 'archived'

type BugSeverity = 'critical' | 'high' | 'medium' | 'low'

const getStatusColorClasses = (status: string) => {
	switch (status?.toLowerCase()) {
		case 'new':
			return {
				bg: 'bg-primary/10 dark:bg-primary/20',
				text: 'text-primary dark:text-primary',
				border: 'border-primary/30 dark:border-primary/40',
			}
		case 'in_progress':
			return {
				bg: 'bg-yellow-100 dark:bg-yellow-900/50',
				text: 'text-yellow-700 dark:text-yellow-300',
				border: 'border-yellow-300 dark:border-yellow-700',
			}
		case 'resolved':
			return {
				bg: 'bg-green-100 dark:bg-green-900/50',
				text: 'text-green-700 dark:text-green-300',
				border: 'border-green-300 dark:border-green-700',
			}
		case 'closed':
			return {
				bg: 'bg-slate-100 dark:bg-slate-700/50',
				text: 'text-slate-600 dark:text-slate-300',
				border: 'border-slate-300 dark:border-slate-600',
			}
		case 'archived':
			return {
				bg: 'bg-purple-100 dark:bg-purple-900/50',
				text: 'text-purple-700 dark:text-purple-300',
				border: 'border-purple-300 dark:border-purple-700',
			}
		default:
			return {
				bg: 'bg-gray-100 dark:bg-gray-800/50',
				text: 'text-gray-700 dark:text-gray-400',
				border: 'border-gray-300 dark:border-gray-600',
			}
	}
}

const getSeverityColorClasses = (severity: string) => {
	switch (severity?.toLowerCase()) {
		case 'critical':
			return {
				bg: 'bg-red-100 dark:bg-red-900/50',
				text: 'text-red-700 dark:text-red-300',
				border: 'border-red-300 dark:border-red-700',
			}
		case 'high':
			return {
				bg: 'bg-orange-100 dark:bg-orange-900/50',
				text: 'text-orange-700 dark:text-orange-300',
				border: 'border-orange-300 dark:border-orange-700',
			}
		case 'medium':
			return {
				bg: 'bg-amber-100 dark:bg-amber-900/50',
				text: 'text-amber-700 dark:text-amber-300',
				border: 'border-amber-300 dark:border-amber-700',
			}
		case 'low':
			return {
				bg: 'bg-lime-100 dark:bg-lime-900/50',
				text: 'text-lime-700 dark:text-lime-300',
				border: 'border-lime-300 dark:border-lime-700',
			}
		default:
			return {
				bg: 'bg-gray-100 dark:bg-gray-800/50',
				text: 'text-gray-700 dark:text-gray-400',
				border: 'border-gray-300 dark:border-gray-600',
			}
	}
}

interface SortableHeaderProps {
	children: React.ReactNode
	sortKey: string
	currentSort?: string
	onSortChange?: (sort: string) => void
	className?: string
}

function SortableHeader({
	children,
	sortKey,
	currentSort,
	onSortChange,
	className,
}: SortableHeaderProps) {
	const isActive = currentSort?.startsWith(sortKey)
	const isDesc = currentSort === `${sortKey}_desc`

	const handleClick = () => {
		if (!onSortChange) return

		if (!isActive) {
			onSortChange(`${sortKey}_desc`)
		} else if (isDesc) {
			onSortChange(`${sortKey}_asc`)
		} else {
			onSortChange(`${sortKey}_desc`)
		}
	}

	return (
		<TableHead
			className={cn(
				'cursor-pointer hover:bg-muted/50 transition-colors select-none',
				className
			)}
			onClick={handleClick}>
			<div className="flex items-center gap-1">
				{children}
				<div className="flex flex-col">
					<ChevronUp
						className={cn(
							'h-3 w-3 transition-colors',
							isActive && !isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
					<ChevronDown
						className={cn(
							'h-3 w-3 -mt-1 transition-colors',
							isActive && isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
				</div>
			</div>
		</TableHead>
	)
}

const getStatusLabel = (status: string, t: (key: string) => string): string => {
	switch (status?.toLowerCase()) {
		case 'new':
			return t('bugReportTable.statusNew')
		case 'in_progress':
			return t('bugReportTable.statusInProgress')
		case 'resolved':
			return t('bugReportTable.statusResolved')
		case 'closed':
			return t('bugReportTable.statusClosed')
		case 'archived':
			return t('bugReportTable.statusArchived')
		default:
			return status.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ')
	}
}

const getSeverityLabel = (severity: string, t: (key: string) => string): string => {
	switch (severity?.toLowerCase()) {
		case 'critical':
			return t('bugReportTable.severityCritical')
		case 'high':
			return t('bugReportTable.severityHigh')
		case 'medium':
			return t('bugReportTable.severityMedium')
		case 'low':
			return t('bugReportTable.severityLow')
		default:
			return severity.charAt(0).toUpperCase() + severity.substring(1)
	}
}

export function BugReportTable({
	bugs,
	isLoading,
	className,
	onBugSelect,
	isPro = true,
	hiddenCount = 0,
	itemsPerPage = 10,
	sort,
	onSortChange,
}: BugReportTableProps) {
	const { t } = useTranslation()
	const [bugToDelete, setBugToDelete] = useState<string | null>(null)
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { currentProgramAccessLevel } = useProgramContext()
	const canDelete = canDeleteFeedback(currentProgramAccessLevel)

	const deleteBugMutation = useMutation({
		mutationFn: async (bugId: string) => {
			const headers = await getAuthHeaders()
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}`, {
				method: 'DELETE',
				headers,
			})
			
			if (!response.ok) {
				const error = await response.json().catch(() => ({ error: 'Failed to delete bug report' }))
				throw new Error(error.error || 'Failed to delete bug report')
			}
			
			return bugId
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['bugs'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
		},
		onError: (error) => {
			console.error('Error deleting bug:', error)
		},
	})

	const updateBugMutation = useMutation({
		mutationFn: async ({
			bugId,
			updates,
		}: {
			bugId: string
			updates: Partial<BugReport> & {
				status?: BugStatus
				severity?: BugSeverity
			}
		}) => {
			const headers = await getAuthHeaders()
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					...headers,
				},
				body: JSON.stringify(updates),
			})

			if (!response.ok) {
				const error = await response.json().catch(() => ({ error: 'Failed to update bug report' }))
				throw new Error(error.error || 'Failed to update bug report')
			}

			return response.json()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['bugs'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
		},
		onError: (error) => {
			console.error('Error updating bug:', error)
		},
	})

	const confirmDelete = () => {
		if (bugToDelete) {
			deleteBugMutation.mutate(bugToDelete)
			setBugToDelete(null)
		}
	}

	if (isLoading && bugs.length === 0) {
		return (
			<Card className={cn('overflow-hidden bg-white dark:bg-slate-900 border-border/50', className)}>
			<div className="w-full overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('bugReportTable.status')}</TableHead>
							<TableHead>{t('bugReportTable.severity')}</TableHead>
							<TableHead className="w-[300px]">{t('bugReportTable.description')}</TableHead>
							<TableHead>{t('bugReportTable.reporter')}</TableHead>
							<TableHead>{t('bugReportTable.screenshot')}</TableHead>
							<TableHead>{t('bugReportTable.reported')}</TableHead>
							<TableHead className="w-[50px]"></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{[...Array(5)].map((_, i) => (
							<TableRow key={i} className="border-b dark:border-slate-700/50">
								<TableCell>
									<Skeleton className="h-6 w-20 rounded-full" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-6 w-20 rounded-full" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-5 w-5/6 rounded" />
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<Skeleton className="h-6 w-6 rounded-full" />
										<Skeleton className="h-4 w-24 rounded" />
									</div>
								</TableCell>
								<TableCell>
									<Skeleton className="h-8 w-8 rounded" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-28 rounded" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-8 w-8 rounded" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			</Card>
		)
	}

	const displayedBugs = bugs || []
	const numberOfBlurredRows =
		!isPro && hiddenCount > 0
			? Math.min(3, hiddenCount) // Always show up to 3 blurred rows when there are hidden bugs
			: 0

	if (displayedBugs.length === 0 && numberOfBlurredRows === 0 && !isLoading) {
		return (
			<Card
				className={cn(
					'bg-white dark:bg-slate-900 border-border/50',
					className
				)}>
				<CardContent>
					<div className="text-center py-12 text-muted-foreground">
						<Bug className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
						<h3 className="text-lg font-semibold">{t('bugReportTable.noBugReports')}</h3>
						<p className="mt-1 text-sm">
							{t('bugReportTable.noBugsMatchingFilters')}
						</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<>
			<Card className={cn('overflow-hidden bg-white dark:bg-slate-900 border-border/50 relative', className)}>
			<div className="w-full overflow-x-auto relative">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent border-t-0 bg-slate-50/50 dark:bg-slate-800/50 sticky top-0 z-10">
							<SortableHeader
								sortKey="status"
								currentSort={sort}
								onSortChange={onSortChange}>
								{t('bugReportTable.status')}
							</SortableHeader>
							<SortableHeader
								sortKey="severity"
								currentSort={sort}
								onSortChange={onSortChange}>
								{t('bugReportTable.severity')}
							</SortableHeader>
							<TableHead className="min-w-[300px]">{t('bugReportTable.description')}</TableHead>
							<TableHead className="min-w-[150px]">{t('bugReportTable.reporter')}</TableHead>
							<TableHead className="min-w-[100px]">{t('bugReportTable.screenshot')}</TableHead>
							<SortableHeader
								sortKey="created_at"
								currentSort={sort}
								onSortChange={onSortChange}
								className="min-w-[150px]">
								{t('bugReportTable.reported')}
							</SortableHeader>
							<TableHead className="w-[50px]"></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{!isPro &&
							numberOfBlurredRows > 0 &&
							Array.from({ length: numberOfBlurredRows }).map((_, index) => {
								// Generate realistic placeholder data for hidden bugs (showing newest first)
								const placeholderBugs = [
									{
										severity: 'Critical',
										status: 'New',
										description:
											'Critical payment gateway failure causing transaction errors for premium users.',
										reporter: 'Sarah Johnson',
										created: '2 hours ago',
									},
									{
										severity: 'High',
										status: 'New',
										description:
											'Database connection timeouts affecting user login and registration systems.',
										reporter: 'Mike Chen',
										created: '30 minutes ago',
									},
									{
										severity: 'Critical',
										status: 'New',
										description:
											'Security vulnerability in authentication system discovered by penetration testing.',
										reporter: 'Emily Davis',
										created: '1 hour ago',
									},
									{
										severity: 'High',
										status: 'New',
										description:
											'API endpoints returning 500 errors for mobile app users during peak hours.',
										reporter: 'Alex Rodriguez',
										created: '45 minutes ago',
									},
									{
										severity: 'Medium',
										status: 'New',
										description:
											'Email notifications not being sent for password reset requests from users.',
										reporter: 'Jennifer Liu',
										created: '1.5 hours ago',
									},
								]

								const placeholder =
									placeholderBugs[index % placeholderBugs.length]

								return (
									<TableRow
										key={`blurred-bug-${index}`}
										className="relative pointer-events-none select-none filter blur-[3px] opacity-30">
										<TableCell className="w-16">
											<Badge
												variant={
													placeholder.severity === 'Critical'
														? 'destructive'
														: placeholder.severity === 'High'
														? 'destructive'
														: placeholder.severity === 'Medium'
														? 'default'
														: 'secondary'
												}
												className="text-xs">
												{placeholder.severity}
											</Badge>
										</TableCell>
										<TableCell className="w-20">
											<Badge variant="outline" className="text-xs">
												{placeholder.status}
											</Badge>
										</TableCell>
										<TableCell className="max-w-md">
											<div className="space-y-1">
												<p className="text-sm font-medium line-clamp-2">
													{placeholder.description}
												</p>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Avatar className="h-7 w-7">
													<AvatarFallback className="text-xs bg-muted">
														{placeholder.reporter
															.split(' ')
															.map((n) => n[0])
															.join('')}
													</AvatarFallback>
												</Avatar>
												<span className="text-sm text-muted-foreground">
													{placeholder.reporter}
												</span>
											</div>
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{placeholder.created}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{placeholder.created}
										</TableCell>
										<TableCell>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" className="h-8 w-8 p-0">
														<MoreVertical className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								)
							})}
						{displayedBugs.map((bug) => {
							const statusColors = getStatusColorClasses(bug.status)
							const severityColors = getSeverityColorClasses(bug.severity)
							return (
								<TableRow
									key={bug.id}
									className="cursor-pointer hover:bg-muted/50 transition-colors"
									onClick={(e) => {
										const target = e.target as HTMLElement
										if (
											target.closest('.actions-dropdown-trigger') ||
											target.closest('.screenshot-dialog-trigger') ||
											target.closest('[role="dialog"]') ||
											target.closest('button') ||
											target.closest('[data-radix-collection-item]')
										) {
											return
										}
										onBugSelect?.(bug.id)
									}}>
									<TableCell>
										<Badge
											variant="outline"
											className={cn(
												'capitalize font-normal text-xs px-2 py-0.5',
												statusColors.bg,
												statusColors.text,
												statusColors.border
											)}>
											{getStatusLabel(bug.status, t)}
										</Badge>
									</TableCell>
									<TableCell>
										<Badge
											variant="outline"
											className={cn(
												'capitalize font-normal text-xs px-2 py-0.5',
												severityColors.bg,
												severityColors.text,
												severityColors.border
											)}>
											{getSeverityLabel(bug.severity, t)}
										</Badge>
									</TableCell>
									<TableCell className="font-medium text-sm">
										{bug.description.length > 170
											? `${bug.description.slice(0, 170)}...`
											: bug.description}
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-2">
											<Avatar className="h-7 w-7">
												<AvatarFallback className="text-xs">
													{(bug.reporter_name || bug.reporter_email || 'A')
														.charAt(0)
														.toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<span className="text-xs truncate max-w-[120px] text-muted-foreground group-hover:text-foreground">
												{bug.reporter_name || bug.reporter_email || t('bugReportTable.anonymous')}
											</span>
										</div>
									</TableCell>
									<TableCell>
										{bug.screenshot_url ? (
											<Dialog>
												<DialogTrigger asChild>
													<div className="screenshot-dialog-trigger group cursor-pointer">
														<img
															src={bug.screenshot_url}
															alt={t('bugReportTable.bugScreenshot')}
															className="h-12 w-16 object-cover rounded border border-border/50 hover:border-primary/50 transition-colors"
															onClick={(e) => {
																e.stopPropagation()
															}}
														/>
													</div>
												</DialogTrigger>
												<DialogContent className="max-w-[90vw] max-h-[90vh] w-screen h-screen p-0 bg-black/95 border-none shadow-none flex items-center justify-center [&_button_svg]:text-white [&_button]:absolute [&_button]:top-4 [&_button]:right-4 [&_button]:z-50">
													<img
														src={bug.screenshot_url}
														alt={t('bugReportTable.bugScreenshot')}
														className="max-w-full max-h-full object-contain"
													/>
												</DialogContent>
											</Dialog>
										) : (
											<span className="text-muted-foreground text-xs italic">
												{t('bugReportTable.none')}
											</span>
										)}
									</TableCell>
									<TableCell className="text-muted-foreground text-xs">
										{formatDistanceToNow(new Date(bug.created_at), {
											addSuffix: true,
										})}
									</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger
												asChild
												className="actions-dropdown-trigger">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													onClick={(e) => e.stopPropagation()}>
													<MoreVertical className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onBugSelect?.(bug.id)
													}}>
													{t('bugReportTable.viewDetails')}
												</DropdownMenuItem>
												<DropdownMenuSub>
													<DropdownMenuSubTrigger>
														{t('bugReportTable.changeStatus')}
													</DropdownMenuSubTrigger>
													<DropdownMenuSubContent>
														{(
															[
																'new',
																'in_progress',
																'resolved',
																'closed',
																'archived',
															] as BugStatus[]
														).map((st) => (
															<DropdownMenuItem
																key={st}
																onClick={(e) => {
																	e.stopPropagation()
																	updateBugMutation.mutate({
																		bugId: bug.id,
																		updates: { status: st },
																	})
																}}
																disabled={
																	bug.status === st ||
																	updateBugMutation.isPending
																}>
																{getStatusLabel(st, t)}
															</DropdownMenuItem>
														))}
													</DropdownMenuSubContent>
												</DropdownMenuSub>
												<DropdownMenuSub>
													<DropdownMenuSubTrigger>
														{t('bugReportTable.changeSeverity')}
													</DropdownMenuSubTrigger>
													<DropdownMenuSubContent>
														{(
															[
																'critical',
																'high',
																'medium',
																'low',
															] as BugSeverity[]
														).map((sev) => (
															<DropdownMenuItem
																key={sev}
																onClick={(e) => {
																	e.stopPropagation()
																	updateBugMutation.mutate({
																		bugId: bug.id,
																		updates: { severity: sev },
																	})
																}}
																disabled={
																	bug.severity === sev ||
																	updateBugMutation.isPending
																}>
																{getSeverityLabel(sev, t)}
															</DropdownMenuItem>
														))}
													</DropdownMenuSubContent>
												</DropdownMenuSub>
												{canDelete && (
													<>
														<DropdownMenuSeparator />
														<DropdownMenuItem
															className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-700/20 dark:focus:text-red-500"
															onClick={(e) => {
																e.stopPropagation()
																setBugToDelete(bug.id)
															}}
															disabled={deleteBugMutation.isPending}>
															{t('bugReportTable.deleteReport')}
														</DropdownMenuItem>
													</>
												)}
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>

				{/* Upgrade Button Overlay - positioned in center of blurred rows */}
				{!isPro && numberOfBlurredRows > 0 && (
					<div
						className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-30"
						style={{
							top: `${66 + (numberOfBlurredRows * 65) / 2 - 25}px`, // 66px for header + half of blurred rows - half button height
							height: '50px',
						}}>
						<div className="pointer-events-auto">
							<Button
								variant="default"
								size="lg"
								className="upgrade-button shadow-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold animate-pulse border-2 border-primary-foreground/20"
								onClick={(e) => {
									e.stopPropagation()
									navigate('/settings?tab=billing')
								}}>
								<Lock className="h-5 w-5 mr-2.5" />
								{t('bugReportTable.upgradeToView')}{' '}
								{hiddenCount > 1
									? `${hiddenCount} ${t('bugReportTable.moreBugReports')}`
									: t('bugReportTable.moreBugReports')}
							</Button>
						</div>
					</div>
				)}
			</div>
			</Card>
			<AlertDialog
				open={!!bugToDelete}
				onOpenChange={() => setBugToDelete(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('bugReportTable.areYouSure')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('bugReportTable.deleteBugConfirmation')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteBugMutation.isPending}>
							{t('bugReportTable.cancel')}
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white"
							onClick={confirmDelete}
							disabled={deleteBugMutation.isPending}>
							{deleteBugMutation.isPending ? t('bugReportTable.deleting') : t('bugReportTable.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
